import {
  safeWidgetLanguage,
  type WidgetLanguageCode,
} from "./widgets/ferry-widget-localization";

export type FerryWidgetConfig = {
  fromSlug: string | null;
  toSlug: string | null;
  originEditable: boolean;
  widgetId: string | null;
  theme: FerryWidgetTheme;
  accentColor: string;
  background: FerryWidgetBackground;
  language: WidgetLanguageCode;
  /** Absent deliberately preserves the inherited Teravia typography. */
  fontPreset?: FerryWidgetFontPreset;
  /** Absent/native deliberately preserves the existing HTML select controls. */
  stationPicker?: FerryWidgetStationPicker;
};

export type FerryWidgetTheme = "light" | "dark" | "auto";
/** `standard` was the public V1 value; parsing keeps it compatible below. */
export type FerryWidgetBackground = "soft" | "white" | "transparent";
export const FERRY_WIDGET_FONT_PRESETS = [
  "josefin-sans",
  "inter",
  "montserrat",
  "open-sans",
  "jost",
  "league-spartan",
] as const;
export type FerryWidgetFontPreset = (typeof FERRY_WIDGET_FONT_PRESETS)[number];
export const FERRY_WIDGET_FONT_OPTIONS: ReadonlyArray<{
  value: FerryWidgetFontPreset | undefined;
  label: string;
}> = [
  { value: undefined, label: "Teravia default" },
  { value: "josefin-sans", label: "Josefin Sans" },
  { value: "inter", label: "Inter" },
  { value: "montserrat", label: "Montserrat" },
  { value: "open-sans", label: "Open Sans" },
  { value: "jost", label: "Jost" },
  { value: "league-spartan", label: "League Spartan" },
];
export const FERRY_WIDGET_STATION_PICKERS = ["native", "teravia"] as const;
export type FerryWidgetStationPicker =
  (typeof FERRY_WIDGET_STATION_PICKERS)[number];
export const FERRY_WIDGET_STATION_PICKER_OPTIONS: ReadonlyArray<{
  value: FerryWidgetStationPicker;
  label: string;
}> = [
  { value: "native", label: "Native" },
  { value: "teravia", label: "Teravia" },
];

type QueryValue = string | string[] | undefined;

export const DEFAULT_FERRY_WIDGET_CONFIG: FerryWidgetConfig = {
  fromSlug: null,
  toSlug: null,
  originEditable: true,
  widgetId: null,
  theme: "light",
  accentColor: "#31595A",
  background: "soft",
  language: "auto",
  stationPicker: "native",
};

function singleValue(value: QueryValue) {
  return typeof value === "string" ? value : null;
}

export function safeFerryStopSlug(value: unknown) {
  const slug = typeof value === "string" ? value.trim().toLowerCase() : "";
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length <= 64
    ? slug
    : null;
}

export function safeFerryWidgetId(value: unknown) {
  const widgetId = typeof value === "string" ? value.trim() : "";
  return /^tw_[A-Za-z0-9_-]{16,64}$/.test(widgetId) ? widgetId : null;
}

export function safeFerryWidgetTheme(value: unknown): FerryWidgetTheme {
  return value === "light" || value === "dark" || value === "auto"
    ? value
    : DEFAULT_FERRY_WIDGET_CONFIG.theme;
}

export function safeFerryWidgetBackground(
  value: unknown,
): FerryWidgetBackground {
  if (value === "transparent" || value === "white" || value === "soft")
    return value;
  // Existing V1 URLs keep their intended standard treatment, now named Soft accent.
  if (value === "standard") return "soft";
  return DEFAULT_FERRY_WIDGET_CONFIG.background;
}

/** An unrecognised value must not change legacy widget typography. */
export function safeFerryWidgetFontPreset(
  value: unknown,
): FerryWidgetFontPreset | undefined {
  return typeof value === "string" &&
    (FERRY_WIDGET_FONT_PRESETS as readonly string[]).includes(value)
    ? (value as FerryWidgetFontPreset)
    : undefined;
}

export function ferryWidgetFontClassName(
  fontPreset: FerryWidgetFontPreset | undefined,
) {
  return fontPreset ? `teravia-ferry-font-${fontPreset}` : "";
}

/** Missing and unrecognised values must preserve the existing native selects. */
export function safeFerryWidgetStationPicker(
  value: unknown,
): FerryWidgetStationPicker {
  return value === "teravia" ? "teravia" : "native";
}

export function safeFerryWidgetAccentColor(value: unknown) {
  const raw = typeof value === "string" ? value.trim() : "";
  const expanded = /^#[0-9a-fA-F]{3}$/.test(raw)
    ? `#${raw
        .slice(1)
        .split("")
        .map((character) => `${character}${character}`)
        .join("")}`
    : raw;
  return /^#[0-9a-fA-F]{6}$/.test(expanded)
    ? expanded.toUpperCase()
    : DEFAULT_FERRY_WIDGET_CONFIG.accentColor;
}

export function createFerryWidgetId() {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
    const value = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
      "",
    );
    return `tw_${btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")}`;
  }
  // This is only a legacy-browser fallback. Modern partner browsers use Web Crypto above.
  return `tw_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function parseFerryWidgetConfig(
  searchParams: Record<string, QueryValue>,
): FerryWidgetConfig {
  const fromSlug = safeFerryStopSlug(singleValue(searchParams.from));
  const possibleToSlug = safeFerryStopSlug(singleValue(searchParams.to));
  const editableValue = singleValue(searchParams.originEditable);
  const originEditable =
    editableValue === "0" || editableValue === "false" ? false : true;

  return {
    fromSlug,
    toSlug: possibleToSlug === fromSlug ? null : possibleToSlug,
    originEditable,
    widgetId: safeFerryWidgetId(singleValue(searchParams.widgetId)),
    theme: safeFerryWidgetTheme(singleValue(searchParams.theme)),
    accentColor: safeFerryWidgetAccentColor(singleValue(searchParams.accent)),
    background: safeFerryWidgetBackground(singleValue(searchParams.background)),
    language: safeWidgetLanguage(singleValue(searchParams.lang)),
    fontPreset: safeFerryWidgetFontPreset(singleValue(searchParams.font)),
    stationPicker: safeFerryWidgetStationPicker(
      singleValue(searchParams.picker),
    ),
  };
}

function trimBase(base: string) {
  return base.replace(/\/$/, "");
}

export function ferryWidgetEmbedUrl(config: FerryWidgetConfig, base: string) {
  const params = new URLSearchParams();
  if (config.fromSlug) params.set("from", config.fromSlug);
  if (config.toSlug) params.set("to", config.toSlug);
  params.set("originEditable", config.originEditable ? "true" : "false");
  if (config.widgetId) params.set("widgetId", config.widgetId);
  params.set("theme", config.theme);
  params.set("accent", config.accentColor);
  params.set("background", config.background);
  params.set("lang", config.language);
  if (config.fontPreset) params.set("font", config.fontPreset);
  if (config.stationPicker === "teravia") params.set("picker", "teravia");
  return `${trimBase(base)}/lake-como/widgets/ferry-planner/embed?${params.toString()}`;
}

export function fullFerryPlannerUrl(
  config: Pick<FerryWidgetConfig, "fromSlug" | "toSlug">,
  base: string,
) {
  return `${trimBase(base)}${fullFerryPlannerHref(config.fromSlug, config.toSlug)}`;
}

function attributesForEmbed(config: FerryWidgetConfig) {
  return [
    `data-teravia-widget="lake-como-ferry"`,
    config.widgetId ? `data-widget-id="${config.widgetId}"` : null,
    config.fromSlug ? `data-from="${config.fromSlug}"` : null,
    config.toSlug ? `data-to="${config.toSlug}"` : null,
    `data-origin-editable="${config.originEditable ? "true" : "false"}"`,
    `data-theme="${config.theme}"`,
    `data-accent-color="${config.accentColor}"`,
    `data-background="${config.background}"`,
    `data-language="${config.language}"`,
    config.fontPreset ? `data-font="${config.fontPreset}"` : null,
    config.stationPicker === "teravia" ? `data-picker="teravia"` : null,
  ]
    .filter(Boolean)
    .join("\n  ");
}

export function ferryWidgetScriptEmbedCode(
  config: FerryWidgetConfig,
  base: string,
) {
  const fallback = fullFerryPlannerUrl(config, base);
  // V5 refreshes the loader boot protocol. Older snippets at the unversioned,
  // V2, V3 and V4 URLs still receive the same backwards-compatible endpoint.
  const loader = `${trimBase(base)}/widgets/embed.js?v=5`;
  return [
    "<div",
    `  ${attributesForEmbed(config)}`,
    ">",
    `  <a href="${fallback}" hidden>Open the Lake Como ferry planner</a>`,
    "</div>",
    "<script async",
    `  src="${loader}">`,
    "</script>",
  ].join("\n");
}

export function ferryWidgetEmbedCode(config: FerryWidgetConfig, base: string) {
  const src = ferryWidgetEmbedUrl(config, base);
  return [
    "<iframe",
    `  src=\"${src}\"`,
    '  width="100%"',
    '  height="780"',
    '  loading="lazy"',
    '  title="Lake Como Ferry Planner by Teravia"',
    '  referrerpolicy="strict-origin-when-cross-origin"',
    '  sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"',
    '  scrolling="no"',
    '  style="width:100%;max-width:680px;display:block;margin:0 auto;border:0;overflow:hidden;background:transparent;"',
    "></iframe>",
  ].join("\n");
}

export function ferryWidgetAttributionCode(base: string) {
  const plannerUrl = `${trimBase(base)}/transport/lake-como/ferries`;
  return `<p>Lake Como ferry planning powered by <a href=\"${plannerUrl}\">Teravia</a>.</p>`;
}

export function fullFerryPlannerHref(
  fromSlug: string | null,
  toSlug: string | null,
  date?: string | null,
  afterTime?: string | null,
  widgetId?: string | null,
) {
  const params = new URLSearchParams();
  if (fromSlug) params.set("from", fromSlug);
  if (toSlug) params.set("to", toSlug);
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) params.set("date", date);
  if (afterTime && /^([01]\d|2[0-3]):[0-5]\d$/.test(afterTime) && fromSlug && toSlug && date) {
    params.set("after", afterTime);
    params.set("search", "1");
  }
  if (widgetId && /^[a-z0-9_-]{1,80}$/i.test(widgetId)) params.set("widget_id", widgetId);
  const query = params.toString();
  return `/transport/lake-como/ferries${query ? `?${query}` : ""}`;
}

export function parseFerryPlannerDeepLink(params: URLSearchParams) {
  const from = params.get("from")?.trim().toLowerCase() || "";
  const to = params.get("to")?.trim().toLowerCase() || "";
  const date = params.get("date") || "";
  const after = params.get("after") || "";
  const parsedDate = new Date(`${date}T12:00:00Z`);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(parsedDate.getTime()) && parsedDate.toISOString().slice(0, 10) === date;
  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(after);
  return {
    from, to,
    date: validDate ? date : null,
    after: validTime ? after : null,
    autoSearch: params.get("search") === "1" && Boolean(from && to && from !== to && validDate && validTime),
    widgetId: /^[a-z0-9_-]{1,80}$/i.test(params.get("widget_id") || "") ? params.get("widget_id") : null,
  };
}
